/**
 * Utility functions for date/time conversion between local time and UTC
 * Used for all transactions except Razorpay transactions
 */

/**
 * Convert local date/time to UTC for sending to backend
 * @param {Date|string} localDateTime - Local date/time
 * @returns {string} - UTC ISO string
 */
export const convertLocalToUTC = (localDateTime) => {
  if (!localDateTime) return null;
  
  // Convert to string if it's not already a string, then create Date object
  const dateString = String(localDateTime);
  if (!dateString.trim()) return null;
  
  const date = new Date(localDateTime);
  if (isNaN(date.getTime())) return null;
  
  return date.toISOString();
};

/**
 * Convert UTC date/time from backend to local time for display
 * @param {string|Date} utcDateTime - UTC ISO string or Date object from backend
 * @returns {Date} - Local date object
 */
export const convertUTCToLocal = (utcDateTime) => {
  if (!utcDateTime) return null;
  
  // If it's already a Date object, it's already in local time (JavaScript Date objects are always local)
  if (utcDateTime instanceof Date) {
    return isNaN(utcDateTime.getTime()) ? null : utcDateTime;
  }
  
  // Convert to string if it's not already a string
  let utcString = String(utcDateTime);
  
  // If the string is empty or just whitespace, return null
  if (!utcString.trim()) return null;
  
  // Handle different timestamp formats from the API
  // Format 1: "2025-09-24 05:33:18.619000" (without timezone info)
  // Format 2: "2025-09-24T05:33:18.619Z" (with Z)
  // Format 3: "2025-09-24T05:33:18.619+00:00" (with offset)
  
  // If it's in format "YYYY-MM-DD HH:mm:ss" or "YYYY-MM-DD HH:mm:ss.ssssss", treat as UTC
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(\.\d+)?$/.test(utcString)) {
    // Convert to ISO format and add Z to indicate UTC
    utcString = utcString.replace(' ', 'T') + 'Z';
  }
  // If it doesn't have timezone info and is in ISO format, add Z
  else if (!utcString.endsWith('Z') && !utcString.includes('+') && !utcString.includes('-', 10)) {
    utcString = utcString + 'Z';
  }
  
  // Create Date object from UTC string - JavaScript automatically converts to local time
  const date = new Date(utcString);
  if (isNaN(date.getTime())) return null;
  
  // The Date object is now in local time, but we need to ensure it represents the correct local date
  // For filtering purposes, we want to compare the local date, not the UTC date
  return date;
};

/**
 * Format UTC date/time from backend to local time string
 * @param {string|Date} utcDateTime - UTC ISO string or Date object from backend
 * @param {string} format - Format type ('date', 'time', 'datetime', 'iso')
 * @returns {string} - Formatted local time string
 */
export const formatUTCToLocal = (utcDateTime, format = 'datetime') => {
  if (!utcDateTime) return '-';
  
  const localDate = convertUTCToLocal(utcDateTime);
  if (!localDate) return '-';
  
  switch (format) {
    case 'date':
      return localDate.toLocaleDateString();
    case 'time':
      return localDate.toLocaleTimeString();
    case 'datetime':
      return localDate.toLocaleString();
    case 'iso':
      return localDate.toISOString();
    default:
      return localDate.toLocaleString();
  }
};

/**
 * Get current local date/time as UTC string for API calls
 * @returns {string} - Current UTC ISO string
 */
export const getCurrentUTC = () => {
  return new Date().toISOString();
};

/**
 * Convert local date/time input to UTC for form submission
 * @param {string} localDateTimeString - Local date/time string (e.g., "2024-01-15T10:30")
 * @returns {string} - UTC ISO string
 */
export const convertFormDateTimeToUTC = (localDateTimeString) => {
  if (!localDateTimeString) return null;
  
  // Convert to string if it's not already a string
  const dateString = String(localDateTimeString);
  if (!dateString.trim()) return null;
  
  // Create date object from local time string
  const localDate = new Date(localDateTimeString);
  if (isNaN(localDate.getTime())) return null;
  
  return localDate.toISOString();
};

/**
 * Convert UTC date/time from backend to local time for form input
 * @param {string} utcDateTime - UTC ISO string from backend
 * @returns {string} - Local date/time string for form input
 */
export const convertUTCToFormDateTime = (utcDateTime) => {
  if (!utcDateTime) return '';
  
  // Convert to string if it's not already a string
  const dateString = String(utcDateTime);
  if (!dateString.trim()) return '';
  
  const localDate = convertUTCToLocal(utcDateTime);
  if (!localDate) return '';
  
  // Format for datetime-local input (YYYY-MM-DDTHH:MM)
  const year = localDate.getFullYear();
  const month = String(localDate.getMonth() + 1).padStart(2, '0');
  const day = String(localDate.getDate()).padStart(2, '0');
  const hours = String(localDate.getHours()).padStart(2, '0');
  const minutes = String(localDate.getMinutes()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Check if a transaction is Razorpay transaction
 * @param {Object} transaction - Transaction object
 * @returns {boolean} - True if Razorpay transaction
 */
export const isRazorpayTransaction = (transaction) => {
  return transaction && (
    transaction.payment_type === 'UPI' ||
    transaction.payment_method === 'upi' ||
    transaction.id?.startsWith('pay_') ||
    transaction.paymentId?.startsWith('pay_')
  );
};

/**
 * Convert transaction date/time fields based on transaction type
 * @param {Object} transaction - Transaction object
 * @param {string} direction - 'toUTC' or 'toLocal'
 * @returns {Object} - Transaction with converted date/time fields
 */
export const convertTransactionDateTime = (transaction, direction = 'toLocal') => {
  if (!transaction) return transaction;
  
  // Skip conversion for Razorpay transactions
  if (isRazorpayTransaction(transaction)) {
    return transaction;
  }
  
  const dateTimeFields = [
    'starttime',
    'stoptime',
    'createdAt',
    'updatedAt',
    'created_at',
    'updated_at',
    'startDate',
    'endDate',
    'createdDate',
    'updatedDate'
  ];
  
  const convertedTransaction = { ...transaction };
  
  dateTimeFields.forEach(field => {
    if (convertedTransaction[field]) {
      if (direction === 'toUTC') {
        convertedTransaction[field] = convertLocalToUTC(convertedTransaction[field]);
      } else if (direction === 'toLocal') {
        convertedTransaction[field] = convertUTCToLocal(convertedTransaction[field]);
      }
    }
  });
  
  return convertedTransaction;
};

/**
 * Convert array of transactions date/time fields
 * @param {Array} transactions - Array of transaction objects
 * @param {string} direction - 'toUTC' or 'toLocal'
 * @returns {Array} - Array of transactions with converted date/time fields
 */
export const convertTransactionsDateTime = (transactions, direction = 'toLocal') => {
  if (!Array.isArray(transactions)) return transactions;
  
  return transactions.map(transaction => 
    convertTransactionDateTime(transaction, direction)
  );
};
